import { useState } from 'react'
import { clsx } from 'clsx'
import type { IndicatorConfig, IndicatorType } from '../types'

interface IndicatorPanelProps {
  activeIndicators: IndicatorConfig[]
  onAddIndicator: (config: IndicatorConfig | { type: IndicatorType; params?: Record<string, any> }) => void
  onRemoveIndicator: (id: string) => void
  onUpdateIndicator?: (id: string, updates: Partial<IndicatorConfig>) => void
  onClose?: () => void
}

interface IndicatorCategory {
  name: string
  indicators: {
    type: IndicatorType
    name: string
    description: string
    defaultParams?: Record<string, any>
  }[]
}

const indicatorCategories: IndicatorCategory[] = [
  {
    name: 'Moving Averages',
    indicators: [
      { type: 'SMA', name: 'Simple Moving Average', description: 'Average price over N periods' },
      { type: 'EMA', name: 'Exponential Moving Average', description: 'Weighted average emphasizing recent prices' },
      { type: 'WMA', name: 'Weighted Moving Average', description: 'Linear weighted average' },
      { type: 'DEMA', name: 'Double EMA', description: 'Reduces lag of single EMA' },
      { type: 'TEMA', name: 'Triple EMA', description: 'Further reduces lag' },
      { type: 'HMA', name: 'Hull Moving Average', description: 'Very smooth, low lag MA' },
      { type: 'VWMA', name: 'Volume Weighted MA', description: 'MA weighted by volume' },
    ],
  },
  {
    name: 'Oscillators',
    indicators: [
      { type: 'RSI', name: 'Relative Strength Index', description: 'Momentum oscillator (0-100)' },
      { type: 'MACD', name: 'MACD', description: 'Trend-following momentum indicator' },
      { type: 'STOCH', name: 'Stochastic', description: 'Compares closing price to range' },
      { type: 'STOCHRSI', name: 'Stochastic RSI', description: 'RSI applied to RSI values' },
      { type: 'CCI', name: 'Commodity Channel Index', description: 'Identifies cyclical trends' },
      { type: 'MFI', name: 'Money Flow Index', description: 'Volume-weighted RSI' },
      { type: 'WILLR', name: 'Williams %R', description: 'Overbought/oversold indicator' },
      { type: 'ROC', name: 'Rate of Change', description: 'Price momentum percentage' },
      { type: 'MOM', name: 'Momentum', description: 'Price change over N periods' },
      { type: 'AO', name: 'Awesome Oscillator', description: 'Market momentum histogram' },
    ],
  },
  {
    name: 'Volatility',
    indicators: [
      { type: 'BB', name: 'Bollinger Bands', description: 'Volatility bands around MA' },
      { type: 'ATR', name: 'Average True Range', description: 'Measures market volatility' },
      { type: 'KC', name: 'Keltner Channel', description: 'ATR-based volatility bands' },
      { type: 'DC', name: 'Donchian Channel', description: 'Highest high/lowest low channel' },
    ],
  },
  {
    name: 'Volume',
    indicators: [
      { type: 'VOL', name: 'Volume', description: 'Trading volume histogram' },
      { type: 'VWAP', name: 'VWAP', description: 'Volume Weighted Average Price' },
      { type: 'OBV', name: 'On-Balance Volume', description: 'Cumulative volume indicator' },
      { type: 'ADL', name: 'Accumulation/Distribution', description: 'Money flow volume' },
      { type: 'CMF', name: 'Chaikin Money Flow', description: 'Volume-weighted A/D' },
    ],
  },
  {
    name: 'Trend',
    indicators: [
      { type: 'ADX', name: 'Average Directional Index', description: 'Trend strength indicator' },
      { type: 'DMI', name: 'Directional Movement', description: '+DI and -DI lines' },
      { type: 'PSAR', name: 'Parabolic SAR', description: 'Stop and reverse points' },
      { type: 'SUPERTREND', name: 'SuperTrend', description: 'Trend following overlay' },
      { type: 'ICHIMOKU', name: 'Ichimoku Cloud', description: 'Complete trading system' },
      { type: 'AROON', name: 'Aroon', description: 'Trend direction and strength' },
    ],
  },
  {
    name: 'Support/Resistance',
    indicators: [
      { type: 'PIVOTS', name: 'Pivot Points', description: 'Auto S/R levels' },
      { type: 'FIBO_AUTO', name: 'Auto Fibonacci', description: 'Auto-drawn Fib levels' },
    ],
  },
]

export function IndicatorPanel({ 
  activeIndicators, 
  onAddIndicator, 
  onRemoveIndicator,
  onUpdateIndicator,
  onClose,
}: IndicatorPanelProps) {
  const [search, setSearch] = useState('')
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Moving Averages')

  const filteredCategories = indicatorCategories.map(cat => ({
    ...cat,
    indicators: cat.indicators.filter(
      ind => 
        ind.name.toLowerCase().includes(search.toLowerCase()) ||
        ind.type.toLowerCase().includes(search.toLowerCase()) ||
        ind.description.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.indicators.length > 0)

  const isIndicatorActive = (type: IndicatorType) => 
    activeIndicators.some(i => i.type === type)

  return (
    <div className="w-80 bg-kit-card border-l border-kit-border flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-kit-border">
        <h2 className="text-lg font-bold text-white">Indicators</h2>
        {onClose && (
          <button 
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="p-4 border-b border-kit-border">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search indicators..."
          className="w-full px-3 py-2 bg-kit-darker border border-kit-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-kit-accent"
        />
      </div>

      {/* Active Indicators */}
      {activeIndicators.length > 0 && (
        <div className="p-4 border-b border-kit-border">
          <h3 className="text-sm font-semibold text-gray-400 mb-2">Active Indicators</h3>
          <div className="flex flex-wrap gap-2">
            {activeIndicators.map((indicator) => (
              <div 
                key={indicator.id}
                className="flex items-center gap-2 px-2 py-1 bg-kit-darker rounded-md"
              >
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: indicator.color }}
                />
                <span className="text-sm text-white">{indicator.type}</span>
                <button
                  onClick={() => onRemoveIndicator(indicator.id)}
                  className="text-gray-400 hover:text-kit-red"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Indicator List */}
      <div className="flex-1 overflow-y-auto">
        {filteredCategories.map((category) => (
          <div key={category.name}>
            <button
              onClick={() => setExpandedCategory(
                expandedCategory === category.name ? null : category.name
              )}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-kit-darker transition-colors"
            >
              <span className="font-medium text-white">{category.name}</span>
              <svg 
                className={clsx(
                  'w-4 h-4 text-gray-400 transition-transform',
                  expandedCategory === category.name && 'rotate-180'
                )} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedCategory === category.name && (
              <div className="pb-2">
                {category.indicators.map((indicator) => {
                  const isActive = isIndicatorActive(indicator.type)
                  return (
                    <button
                      key={indicator.type}
                      onClick={() => {
                        if (isActive) {
                          const existing = activeIndicators.find(i => i.type === indicator.type)
                          if (existing) onRemoveIndicator(existing.id)
                        } else {
                          onAddIndicator(indicator.type, indicator.defaultParams)
                        }
                      }}
                      className={clsx(
                        'w-full px-4 py-2 flex items-start gap-3 text-left transition-colors',
                        isActive ? 'bg-kit-accent/10' : 'hover:bg-kit-darker'
                      )}
                    >
                      <div 
                        className={clsx(
                          'mt-1 w-4 h-4 rounded border flex items-center justify-center',
                          isActive 
                            ? 'bg-kit-accent border-kit-accent' 
                            : 'border-gray-500'
                        )}
                      >
                        {isActive && (
                          <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{indicator.name}</span>
                          <span className="text-xs text-gray-500">{indicator.type}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{indicator.description}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Custom Indicator */}
      <div className="p-4 border-t border-kit-border">
        <button className="w-full btn btn-secondary flex items-center justify-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Custom Indicator
        </button>
      </div>
    </div>
  )
}
